import { useRef, useState } from 'react'
import type { JSX } from 'react'
import { clsx } from 'clsx'

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  UnstyledButton,
} from '@buffer-mono/popcorn'

import type { Image } from '~publish/legacy/composer/composer/entities/factories'

import styles from './ImageCarousel.module.css'

// No behavior option: leaving it at the default hands the decision to the
// track's own scroll-behavior, which drops to auto under reduced motion.
const scrollByOneSlide = (track: HTMLElement, direction: 1 | -1): void => {
  const slide = track.firstElementChild
  const step =
    slide instanceof HTMLElement ? slide.offsetWidth : track.clientWidth
  track.scrollBy({ left: step * direction })
}

export const ImageCarousel = ({
  images,
  className,
}: {
  images: Image[]
  className?: string
}): JSX.Element => {
  const trackRef = useRef<HTMLElement>(null)
  const [canScrollBack, setCanScrollBack] = useState(false)
  // jsdom and the first paint both report a zero-width track, so the forward
  // affordance starts from the image count instead of the scroll geometry.
  const [canScrollForward, setCanScrollForward] = useState(images.length > 1)

  const handleScroll = (): void => {
    const track = trackRef.current
    if (!track) return

    setCanScrollBack(track.scrollLeft > 0)
    setCanScrollForward(
      track.scrollLeft + track.clientWidth < track.scrollWidth - 1,
    )
  }

  const scroll = (direction: 1 | -1) => (): void => {
    if (trackRef.current) scrollByOneSlide(trackRef.current, direction)
  }

  return (
    <div className={clsx(styles.root, className)}>
      {/* The named section gives the strip a landmark; the buttons beside it
          are what carry keyboard access, so it needs no tab stop of its own. */}
      <section
        aria-label={`${images.length} images`}
        aria-roledescription="carousel"
        className={styles.track}
        data-testid="preview-images"
        onScroll={handleScroll}
        ref={trackRef}
      >
        {images.map((image, index) => (
          <img
            alt={image.altText || `Image ${index + 1} of ${images.length}`}
            className={styles.slide}
            key={image.url}
            src={image.url}
          />
        ))}
      </section>

      {canScrollBack && (
        <UnstyledButton
          aria-label="Previous image"
          className={clsx(styles.nav, styles.previous)}
          onClick={scroll(-1)}
        >
          <ChevronLeftIcon size="small" />
        </UnstyledButton>
      )}

      {canScrollForward && (
        <UnstyledButton
          aria-label="Next image"
          className={clsx(styles.nav, styles.next)}
          onClick={scroll(1)}
        >
          <ChevronRightIcon size="small" />
        </UnstyledButton>
      )}
    </div>
  )
}
